/**
 * Chatbot component
 *
 * Interactive chatbot interface that communicates with Azure OpenAI backend
 */

import React, { useState, useRef, useEffect } from "react";

// Render inline markdown: bold, links. Returns array of React nodes.
function renderInline(text, keyPrefix) {
  const parts = [];
  // Match **bold** or [label](url)
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/\S+)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={`${keyPrefix}-b${i}`}>{m[1]}</strong>);
    else if (m[2] !== undefined) parts.push(<a key={`${keyPrefix}-a${i}`} href={m[3]} target="_blank" rel="noopener noreferrer">{m[2]}</a>);
    else if (m[4] !== undefined) parts.push(<a key={`${keyPrefix}-a${i}`} href={m[4]} target="_blank" rel="noopener noreferrer">{m[4]}</a>);
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Convert markdown text to React elements (handles paragraphs, bullet lists, bold, links)
function SimpleMarkdown({ children }) {
  if (!children) return null;
  const lines = children.split('\n');
  const elements = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul${key++}`}>{listItems}</ul>);
      listItems = [];
    }
  };

  lines.forEach((line) => {
    const bullet = line.match(/^(\s*[-*])\s+(.*)$/);
    if (bullet) {
      listItems.push(<li key={`li${key++}`}>{renderInline(bullet[2], `li${key}`)}</li>);
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={`p${key++}`}>{renderInline(line.trim(), `p${key}`)}</p>);
    }
  });
  flushList();
  return <>{elements}</>;
}

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();

    if (!input.trim()) return;

    // Add user message to chat
    const userMessage = {
      id: messages.length + 1,
      text: input,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input;
    setInput("");
    setIsLoading(true);

    // Keep focus on input
    setTimeout(() => inputRef.current?.focus(), 0);

    try {
      // Call the backend API
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      console.log("Sending request to backend:", `${backendUrl}/api/chat`);
      console.log("Message:", userInput.substring(0, 50));

      const response = await fetch(`${backendUrl}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userInput,
          conversationId: conversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Status:", response.status);

        // Display user-friendly error from backend
        const errorMessage = {
          id: messages.length + 2,
          text: errorData.error || "Unable to connect to the assistant. Please try again.",
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        return;
      }

      const data = await response.json();
      console.log("Response received:", data);

      if (data.conversationId) setConversationId(data.conversationId);

      const botMessage = {
        id: messages.length + 2,
        text: data.message || "No response from assistant",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Chat error:", error);

      // Show generic error for network issues
      const errorMessage = {
        id: messages.length + 2,
        text: "Unable to connect to the chat service. Please check your internet connection and try again.",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Restore focus to input after response
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <section id="chatbot" className="chatbot-section">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <h2>Chat with AI Assistant</h2>
          <p>Ask me anything about services, projects, or technology</p>
        </div>

        <div className={`chatbot-widget ${isOpen ? "open" : ""}`}>
          <div className="chatbot-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.sender}-message`}>
                <div className="message-content">
                  <div className="message-text">
                    <SimpleMarkdown>{message.text}</SimpleMarkdown>
                  </div>
                  <span className="message-time">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message bot-message">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="chatbot-input-form">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
              className="chatbot-input"
            />
            <button type="submit" disabled={isLoading || !input.trim()} className="chatbot-send-btn">
              {isLoading ? "..." : "Send"}
            </button>
          </form>
        </div>

        <button className="chatbot-toggle-btn" onClick={() => setIsOpen(!isOpen)}>
          <span className="chat-icon">💬</span>
        </button>
      </div>
    </section>
  );
};

export default Chatbot;
